import { useContext, useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Collapse, Spinner } from "reactstrap";
import { _imagesURL, adminBaseURL } from "../../../constants/API";
import Avatar from "../../../assets/images/icon/placeholder_Awatar.png";
import { Language, PostDetailContext } from "../../../App";
import {
  IoThumbsUpOutline,
  IoThumbsUp,
  IoChatbubbleOutline,
  IoBookmarkOutline,
  IoBookmark,
} from "react-icons/io5";
import { AiOutlineRetweet } from "react-icons/ai";
import { RiEditLine } from "react-icons/ri";
import { authorize } from "../../../shared/authorize";
import { useCustomToast } from "../../common/CustomToast";
import axios from "axios";
import { UnixToRelative } from "../../notification/Notification";
import RepostUserModal from "../modals/RepostUserModal";
import usePollRemainingTime from "../../../hooks/usePollRemainingTime";
import { useQueryClient } from "react-query";
import MixpanelManager from "../../../constants/MixpanelManager";
import { MixpanelEvents } from "../../../constants/MixpanelEvents";
import { createPortal } from "react-dom";
import "./PostFooter.css";

const REACTIONS = [
  { id: "like", emoji: "👍", label: "Like", color: "#00BADA" },
  { id: "love", emoji: "❤️", label: "Love", color: "#F43F5E" },
  { id: "celebrate", emoji: "👏", label: "Celebrate", color: "#10B981" },
  { id: "insightful", emoji: "💡", label: "Insightful", color: "#F59E0B" },
  { id: "fire", emoji: "🔥", label: "Amazing", color: "#FF6B00" },
  { id: "wow", emoji: "😮", label: "Wow", color: "#8B5CF6" },
];

const resolveAvatar = (src) => {
  if (!src || src === ".." || src === Avatar) return Avatar;
  if (typeof src !== "string") return Avatar;
  const trimmed = src.trim();
  if (!trimmed) return Avatar;
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("blob:") ||
    trimmed.includes("placeholder_Awatar")
  ) {
    return trimmed;
  }
  const base = _imagesURL.trim().replace(/\/+$/, "");
  const path = trimmed.replace(/^\/+/, "");
  return `${base}/${path}`;
};

const PostFooter = ({
  likes_count,
  setPostId,
  original_id,
  handleLikeUsers,
  like_users_images,
  handleLike,
  isLike,
  handleGetComments,
  comments_count,
  handleRepost,
  isShare,
  isSave,
  handleSave,
  type,
  pollData,
  shares_count,
  value,
}) => {
  const { updatePostDetail } = useContext(PostDetailContext);
  const { lang } = useContext(Language);
  const queryClient = useQueryClient();
  const userData = localStorage.getItem("SignupData");
  const user = userData ? JSON.parse(userData) : null;
  const user_id = user?.data?.id;

  const [isOpen, setIsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [currentReaction, setCurrentReaction] = useState(null);

  const collapseRef = useRef(null);
  const repostButtonRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const leaveTimerRef = useRef(null);

  const pid = original_id || value?.original_id || value?.id;
  const isOwner = value?.user?.id === user_id;
  const isReposted = Boolean(isShare ?? value?.isShare);
  const isSaved = Boolean(isSave ?? value?.isSave);
  const currentSharesCount =
    shares_count ??
    value?.shares_count ??
    value?.share_count ??
    value?.reposts_count ??
    value?.shares ??
    0;

  const toggleModal = useCallback(() => {
    setModalOpen((prev) => !prev);
  }, []);

  const toggleCollapse = () => setIsOpen(!isOpen);

  const getRepostButtonPosition = () => {
    if (repostButtonRef.current) {
      const rect = repostButtonRef.current.getBoundingClientRect();
      return { top: rect.bottom + window.scrollY, left: rect.left };
    }
    return { top: 0, left: 0 };
  };

  const handleRepostClick = (e) => {
    e?.stopPropagation?.();
    if (type !== "poll") {
      if (!isOwner) {
        handleRepost(pid);
      } else {
        setModalOpen(true);
      }
    } else {
      if (isOwner) {
        setModalOpen(true);
      } else {
        if (!isReposted) {
          setIsOpen((prev) => !prev);
        } else {
          handleRepost(pid);
        }
      }
    }
  };

  const handleSaveClick = (e) => {
    e?.stopPropagation?.();
    handleSave(pid);
    MixpanelManager.track(MixpanelEvents.savePost, {
      post_id: pid,
      user_id: user_id,
      is_saved: !isSaved,
    });
  };

  /* ── Reaction Picker Hover Handlers ── */
  const handleLikeMouseEnter = () => {
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setShowReactions(true);
    }, 220);
  };

  const handleLikeMouseLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    leaveTimerRef.current = setTimeout(() => {
      setShowReactions(false);
    }, 320);
  };

  const handleSelectReaction = (e, reaction) => {
    e.stopPropagation();
    setCurrentReaction(reaction);
    setShowReactions(false);
    if (!isLike) {
      handleLike(pid);
    }
    MixpanelManager.track(MixpanelEvents.likePost, {
      post_id: pid,
      user_id: user_id,
      is_liked: true,
      reaction_type: reaction.id,
    });
  };

  const handleDirectLikeClick = () => {
    if (isLike) {
      setCurrentReaction(null);
    } else if (!currentReaction) {
      setCurrentReaction(REACTIONS[1]); // Default Love (❤️)
    }
    handleLike(pid);
    MixpanelManager.track(MixpanelEvents.likePost, {
      post_id: pid,
      user_id: user_id,
      is_liked: !isLike,
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (collapseRef.current && !collapseRef.current.contains(event.target)) {
        setIsOpen(false);
      }
      setShowReactions(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    };
  }, []);

  const self_like_image = user?.data?.image;
  const repostTitle = isOwner
    ? `${currentSharesCount || 0} ${lang?.repost || "Reposts"}`
    : isReposted
    ? lang?.reposted || "Reposted"
    : lang?.repost || "Repost";

  const activeReaction = isLike ? currentReaction || REACTIONS[1] : null;

  return (
    <>
      <div className="mr-post-footer">
        {/* ── Social Proof / Likers & Activity Section ── */}
        {(likes_count > 0 || comments_count > 0 || currentSharesCount > 0) && (
          <div className="mr-social-proof-bar">
            {likes_count > 0 ? (
              <div
                className="mr-likers-trigger"
                onClick={() => {
                  setPostId(pid);
                  handleLikeUsers(pid);
                  MixpanelManager.track(MixpanelEvents.listOfLikes, {
                    post_id: pid,
                    user_id: user_id,
                  });
                }}
                role="button"
                tabIndex={0}
              >
                {/* Overlapping Avatars Stack */}
                <div className="mr-avatar-stack">
                  {like_users_images && like_users_images.length > 0 ? (
                    like_users_images.slice(0, 3).map((imgData, index) => (
                      <div
                        key={`liker-${index}`}
                        className="mr-avatar-item"
                        style={{ zIndex: 5 - index }}
                      >
                        <img
                          src={resolveAvatar(imgData)}
                          alt="Liker"
                          className="mr-avatar-img"
                          onError={(e) => {
                            if (e.target.src !== Avatar && !e.target.src.includes("placeholder_Awatar")) {
                              e.target.src = Avatar;
                            }
                          }}
                          loading="lazy"
                        />
                      </div>
                    ))
                  ) : (
                    <div className="mr-avatar-item" style={{ zIndex: 5 }}>
                      <img
                        src={resolveAvatar(self_like_image)}
                        alt="Liker"
                        className="mr-avatar-img"
                        onError={(e) => {
                          if (e.target.src !== Avatar && !e.target.src.includes("placeholder_Awatar")) {
                            e.target.src = Avatar;
                          }
                        }}
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>

                {/* Instagram-style Social Proof Text */}
                <p className="mr-social-proof-text">
                  {lang?.liked_by || "Liked by"}{" "}
                  {likes_count > 1 ? (
                    <>
                      <strong>{likes_count}</strong> {lang?.people || "people"}
                    </>
                  ) : (
                    <strong>1 {lang?.person || "person"}</strong>
                  )}
                </p>
              </div>
            ) : (
              <div />
            )}

            {/* Comments & Reposts count summary */}
            <div className="d-flex align-items-center gap-1">
              {comments_count > 0 && (
                <span
                  className="mr-summary-chip is-interactive"
                  onClick={() => {
                    setPostId(pid);
                    handleGetComments(pid);
                    updatePostDetail(value);
                  }}
                  title={lang?.comments || "Comments"}
                >
                  {comments_count} {comments_count > 1 ? lang?.comments || "Comments" : lang?.comment || "Comment"}
                </span>
              )}
              {currentSharesCount > 0 && (
                <span className="mr-summary-chip">
                  {comments_count > 0 ? "• " : ""}{currentSharesCount} {currentSharesCount > 1 ? lang?.reposts || "Reposts" : lang?.repost || "Repost"}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Main Actions Bar ── */}
        <div className="mr-actions-row">
          {/* 1. LIKE / EMOJI REACTION BUTTON */}
          <div
            className="mr-action-item"
            onMouseEnter={handleLikeMouseEnter}
            onMouseLeave={handleLikeMouseLeave}
          >
            {/* Floating Reactions Bar (Fly-up on hover) */}
            {showReactions && (
              <div
                className="mr-reactions-container"
                onMouseEnter={handleLikeMouseEnter}
                onMouseLeave={handleLikeMouseLeave}
              >
                {REACTIONS.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className="mr-reaction-btn"
                    onClick={(e) => handleSelectReaction(e, r)}
                    title={r.label}
                  >
                    <span className="mr-reaction-emoji">{r.emoji}</span>
                    <span className="mr-reaction-tooltip">{r.label}</span>
                  </button>
                ))}
              </div>
            )}

            <button
              type="button"
              className={`mr-action-touch mr-action-touch--like ${isLike ? "is-active" : ""}`}
              onClick={handleDirectLikeClick}
              aria-label={isLike ? "Unlike" : "Like"}
              title={isLike ? (activeReaction?.label || "Unlike") : "Like"}
            >
              <div className="mr-icon-aura">
                {isLike && activeReaction ? (
                  <span className="mr-selected-emoji-badge">{activeReaction.emoji}</span>
                ) : (
                  <IoThumbsUpOutline className="mr-like-svg" size={20} />
                )}
              </div>
              <span
                className="mr-count-text"
                style={{ color: isLike && activeReaction ? activeReaction.color : undefined }}
              >
                {likes_count > 0 ? likes_count : ""}
                {likes_count > 0
                  ? ""
                  : isLike && activeReaction
                  ? activeReaction.label
                  : lang?.like || "Like"}
              </span>
            </button>
          </div>

          {/* 2. COMMENT BUTTON */}
          <div className="mr-action-item">
            <button
              type="button"
              className="mr-action-touch mr-action-touch--comment"
              onClick={() => {
                setPostId(pid);
                handleGetComments(pid);
                updatePostDetail(value);
                MixpanelManager.track(MixpanelEvents.addComment, {
                  post_id: pid,
                  user_id: user_id,
                });
              }}
              aria-label="Comment"
              title={lang?.comments || "Comments"}
            >
              <div className="mr-icon-aura">
                <IoChatbubbleOutline size={19} />
              </div>
              <span className="mr-count-text">
                {comments_count > 0 ? comments_count : ""}
                {comments_count > 0 ? "" : lang?.comments || "Comments"}
              </span>
            </button>
          </div>

          {/* 3. REPOST BUTTON */}
          <div className="mr-action-item">
            <button
              type="button"
              ref={repostButtonRef}
              className={`mr-action-touch mr-action-touch--repost ${isReposted ? "is-active" : ""}`}
              onClick={(e) => {
                handleRepostClick(e);
                MixpanelManager.track(MixpanelEvents.repost, {
                  post_id: pid,
                  user_id: user_id,
                  is_reposted: !isReposted,
                });
              }}
              aria-label="Repost"
              title={repostTitle}
            >
              <div className="mr-icon-aura">
                <AiOutlineRetweet className="mr-repost-svg" size={20} />
              </div>
              <span className="mr-count-text">
                {currentSharesCount > 0
                  ? currentSharesCount
                  : isReposted
                  ? lang?.reposted || "Reposted"
                  : lang?.repost || "Repost"}
              </span>
            </button>
          </div>

          {/* 4. SAVE / BOOKMARK BUTTON */}
          <div className="mr-action-item">
            <button
              type="button"
              className={`mr-action-touch mr-action-touch--save ${isSaved ? "is-active" : ""}`}
              onClick={handleSaveClick}
              aria-label={isSaved ? "Unsave post" : "Save post"}
              title={isSaved ? lang?.save_description || "Saved" : lang?.unsave_description || "Save"}
            >
              <div className="mr-icon-aura">
                {isSaved ? (
                  <IoBookmark className="mr-bookmark-svg" size={19} />
                ) : (
                  <IoBookmarkOutline className="mr-bookmark-svg" size={19} />
                )}
              </div>
              <span className="mr-count-text">
                {isSaved ? lang?.saved || "Saved" : lang?.save || "Save"}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Repost Options Popover (For Polls) */}
      <div ref={collapseRef}>
        <Collapse isOpen={isOpen}>
          <div
            style={{
              position: "absolute",
              top: `${getRepostButtonPosition().top + 6}px`,
              left: `${getRepostButtonPosition().left}px`,
              zIndex: 1000,
            }}
          >
            <RepostCard
              repost={handleRepost}
              original_id={pid}
              toggleCollapse={toggleCollapse}
              pollData={pollData || value}
            />
          </div>
        </Collapse>
      </div>

      {modalOpen && (
        <RepostUserModal
          toggleModal={toggleModal}
          modal={modalOpen}
          postId={pid}
        />
      )}
    </>
  );
};

export default PostFooter;

export const RepostCard = ({ repost, original_id, toggleCollapse, pollData }) => {
  const { lang } = useContext(Language);
  const [isPopupOpen, setPopupOpen] = useState(false);

  const handleOpenPopup = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setPopupOpen(true);
  };
  const handleClosePopup = () => setPopupOpen(false);
  const handlePost = () => {
    setPopupOpen(false);
  };

  return (
    <>
      <div className="mr-glass-dropdown">
        {/* Quote Repost Option */}
        <div className="mr-glass-dropdown-item" onClick={handleOpenPopup}>
          <div className="mr-glass-icon-circle">
            <RiEditLine />
          </div>
          <div>
            <p className="mr-glass-item-title">
              {lang.repost_with_your_thoughts || "Quote Repost"}
            </p>
            <p className="mr-glass-item-subtitle">
              {lang.repost_this_poll_with_your_thoughts || "Add your thoughts and share"}
            </p>
          </div>
        </div>

        {/* Instant Repost Option */}
        <div
          className="mr-glass-dropdown-item"
          onClick={() => {
            repost(original_id);
            toggleCollapse();
          }}
        >
          <div className="mr-glass-icon-circle">
            <AiOutlineRetweet size={19} />
          </div>
          <div>
            <p className="mr-glass-item-title">{lang.repost || "Instant Repost"}</p>
            <p className="mr-glass-item-subtitle">
              {lang.repost_this_poll_without_your_thoughts || "Instantly repost to your feed"}
            </p>
          </div>
        </div>
      </div>

      <RepostPollPopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        onPost={handlePost}
        pollData={pollData}
        toggleRepostCard={toggleCollapse}
      />
    </>
  );
};

export const RepostPollPopup = ({
  isOpen,
  onClose,
  onPost,
  pollData,
  toggleRepostCard,
}) => {
  const { lang } = useContext(Language);
  const { showToast } = useCustomToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [caption, setCaption] = useState("");
  const remainingTime = usePollRemainingTime(pollData?.time, pollData?.duration);

  if (!isOpen) return null;

  const repostWithThoughts = async () => {
    if (caption === "") {
      showToast(lang.caption_is_required || "Caption is required", "error");
      return;
    }
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${adminBaseURL}user/post/repost/${pollData?.original_id}`,
        { caption },
        authorize()
      );
      if (response.data.status) {
        setIsLoading(false);
        setCaption("");
        onClose();
        queryClient.invalidateQueries("feedData");
        toggleRepostCard();
        onPost();
        return response.data;
      } else {
        showToast(response.data.message || "Something went wrong.", "error");
        setIsLoading(false);
      }
    } catch (error) {
      showToast(error.message || "An error occurred while reposting.", "error");
      console.error("Error reposting:", error);
      setIsLoading(false);
    }
  };

  const handlePopupClick = (e) => {
    e.stopPropagation();
  };

  const modalContent = (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 10000,
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "auto",
      }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        onClick={handlePopupClick}
      >
        <div
          className="modal-content"
          style={{
            zIndex: 10001,
            borderRadius: "20px",
            border: "1px solid #E2E8F0",
            overflow: "hidden",
            boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
          }}
        >
          <div className="modal-header border-0 pb-0 px-4 pt-4">
            <h5 className="modal-title fs-16 roboto-bold text-dark">
              {lang.repost_poll || "Repost Poll"}
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            />
          </div>
          <div className="modal-body px-4 py-3">
            <textarea
              className="form-control mb-3"
              rows="3"
              placeholder={lang.please_enter_your_thoughts || "What are your thoughts on this poll?"}
              style={{
                resize: "none",
                backgroundColor: "#F8FAFC",
                borderRadius: "14px",
                border: "1px solid #E2E8F0",
                fontSize: "14px",
                padding: "12px 14px",
              }}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              onClick={handlePopupClick}
            />
            <div
              className="p-3 rounded-4"
              style={{ background: "#F8FDFF", border: "1px solid #E0F2FE" }}
            >
              <div className="d-flex align-items-center mb-3">
                <img
                  src={resolveAvatar(pollData?.user?.image)}
                  alt="User"
                  className="rounded-circle me-2 object-cover"
                  width="36px"
                  height="36px"
                  style={{ borderRadius: "50%", aspectRatio: "1 / 1", objectFit: "cover" }}
                />
                <div>
                  <strong className="fs-14 d-block text-dark">
                    {pollData?.user?.name}
                  </strong>
                  <span className="text-muted fs-12">
                    {lang.posted || "Posted"} <UnixToRelative unixTimestamp={pollData?.time} />
                  </span>
                </div>
              </div>
              <p className="fs-14 fw-medium text-dark mb-2">{pollData?.title}</p>
              <div>
                {pollData?.media[0] && (
                  <div
                    className="mt-2 mb-3 d-flex justify-content-center align-items-center rounded-3 overflow-hidden"
                    style={{ maxHeight: "260px", background: "#000" }}
                  >
                    <img
                      src={resolveAvatar(pollData?.media[0])}
                      alt="Poll Preview"
                      className="img-fluid rounded"
                      style={{ maxHeight: "260px", objectFit: "contain" }}
                    />
                  </div>
                )}
                <div className="mt-2">
                  {[
                    pollData?.option_1,
                    pollData?.option_2,
                    pollData?.option_3,
                    pollData?.option_4,
                  ]
                    .filter((data) => data !== "" && data !== null && data !== undefined)
                    .map((option, index) => (
                      <div
                        className="custom-box mb-2 p-2 rounded-3 bg-white border fs-13 fw-medium text-dark"
                        key={index}
                      >
                        {option}
                      </div>
                    ))}
                </div>
              </div>
              <div className="d-flex justify-content-between mt-3 fs-12 text-muted">
                <span>
                  {pollData.votes_count || 0} {lang.votes || "Votes"}
                </span>
                <span>{remainingTime}</span>
              </div>
            </div>
          </div>
          <div className="modal-footer border-0 pt-0 px-4 pb-4 gap-2">
            <button
              type="button"
              className="btn px-4 py-2 rounded-pill fs-13 fw-semibold"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              style={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #CBD5E1",
                color: "#64748B",
              }}
            >
              {lang.cancel || "Cancel"}
            </button>
            <button
              type="button"
              className="btn px-4 py-2 rounded-pill fs-13 fw-semibold text-white"
              onClick={(e) => {
                e.stopPropagation();
                repostWithThoughts();
              }}
              disabled={isLoading}
              style={{
                backgroundColor: "#00BADA",
                border: "none",
                boxShadow: "0 4px 12px rgba(0, 186, 218, 0.25)",
              }}
            >
              {isLoading ? <Spinner size="sm" /> : lang.post || "Post"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
